import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { clubApi } from "../../../club-api-client";
import type { CustomFieldType } from "../../../schemas/custom-field";
import { queryKeys } from "../../query-keys";

export function useClubCustomFields(clubId: string | undefined) {
	return useQuery({
		queryKey: queryKeys.clubPortal.customFields(clubId ?? ""),
		queryFn: () => clubApi.customFields.list(clubId as string),
		enabled: !!clubId,
	});
}

export function useCreateCustomField(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (data: {
			field_key: string;
			label: string;
			field_type: CustomFieldType;
			options?: unknown;
			required?: boolean;
			position?: number;
		}) => clubApi.customFields.create(clubId, data),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: queryKeys.clubPortal.customFields(clubId),
			});
		},
	});
}

export function useUpdateCustomField(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({
			fieldId,
			data,
		}: {
			fieldId: string;
			data: {
				label?: string;
				options?: unknown;
				required?: boolean;
				position?: number;
			};
		}) => clubApi.customFields.update(clubId, fieldId, data),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: queryKeys.clubPortal.customFields(clubId),
			});
		},
	});
}

export function useDeleteCustomField(clubId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (fieldId: string) => clubApi.customFields.delete(clubId, fieldId),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: queryKeys.clubPortal.customFields(clubId),
			});
		},
	});
}

export function useMemberCustomValues(clubId: string | undefined, userId: string | undefined) {
	return useQuery({
		queryKey: queryKeys.clubPortal.memberCustomValues(clubId ?? "", userId ?? ""),
		queryFn: () => clubApi.customValues.list(clubId as string, userId as string),
		enabled: !!clubId && !!userId,
	});
}

export function useSetMemberCustomValue(clubId: string, userId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: ({ fieldId, value }: { fieldId: string; value: unknown }) =>
			clubApi.customValues.set(clubId, userId, fieldId, value),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: queryKeys.clubPortal.memberCustomValues(clubId, userId),
			});
		},
	});
}

export function useDeleteMemberCustomValue(clubId: string, userId: string) {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: (fieldId: string) => clubApi.customValues.delete(clubId, userId, fieldId),
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: queryKeys.clubPortal.memberCustomValues(clubId, userId),
			});
		},
	});
}
